/**
 * Project overview — V4 F-1, `/projects/:key/overview`.
 *
 * The founder's complaint was that a project could not explain itself: opening
 * one dropped you straight onto a board of tasks with no way to learn what the
 * project *is*, who to ask about it, or where its repository lives.
 *
 * This is that one screen — About, type, tech stack, links, contacts,
 * attachments, the Project Guide and the stats row. It is where the project
 * grid now lands; the sidebar still goes to the board, because that is where
 * someone who already knows the project wants to be.
 */

import { useEffect, useState } from 'react';
import { useParams } from 'react-router';
import { ExternalLink, Loader2, Pencil, Plus, X } from 'lucide-react';
import { toast } from 'sonner';
import { PERMISSIONS, PROJECT_TYPES, type ProjectType } from '@virtuetasks/types';
import { PageHeader } from '@/app/layout/AppShell';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { UserAvatar } from '@/components/ui/avatar';
import { RichTextEditor } from '@/components/shared/RichTextEditor';
import { AttachmentSection } from '@/features/tasks';
import { useCan } from '@/features/auth';
import { useUsers } from '@/features/settings';
import { ApiError } from '@/lib/api';
import { ProjectGuideCard } from './ProjectGuideCard';
import {
  useProject,
  useProjectStats,
  useTechStackSuggestions,
  useUpdateProject,
  type ProjectView,
} from './api';

const TYPE_LABEL: Record<ProjectType, string> = {
  client: 'Client work',
  product: 'Product',
  internal: 'Internal',
  research: 'Research',
  marketing: 'Marketing',
  other: 'Other',
};

type About = NonNullable<ProjectView['about']>;

const EMPTY_ABOUT: About = { techStack: [], links: [], contacts: [] };

function Section({
  title,
  action,
  children,
}: {
  title: string;
  action?: React.ReactNode;
  children: React.ReactNode;
}) {
  return (
    <section className="vt-surface flex flex-col gap-2 p-4">
      <div className="flex items-center gap-2">
        <h2 className="flex-1 text-[15px] font-semibold">{title}</h2>
        {action}
      </div>
      {children}
    </section>
  );
}

/** Free-text chips with autocomplete from tags already used here. */
function TechStackEditor({
  value,
  onChange,
}: {
  value: string[];
  onChange: (next: string[]) => void;
}) {
  const { data: suggestions = [] } = useTechStackSuggestions();
  const [draft, setDraft] = useState('');

  function add(tag: string): void {
    const clean = tag.trim().toLowerCase();
    // De-duplicated here as well as on the server, so the chip does not flash
    // in and disappear on save.
    if (!clean || value.includes(clean)) return setDraft('');
    onChange([...value, clean]);
    setDraft('');
  }

  return (
    <div className="flex flex-col gap-1.5">
      <div className="flex flex-wrap gap-1.5">
        {value.map((tag) => (
          <Badge key={tag} tone="neutral">
            {tag}
            <button
              type="button"
              aria-label={`Remove ${tag}`}
              className="ml-1"
              onClick={() => onChange(value.filter((t) => t !== tag))}
            >
              <X className="size-3" />
            </button>
          </Badge>
        ))}
      </div>
      <Input
        aria-label="Add tech"
        data-testid="tech-input"
        list="tech-suggestions"
        placeholder="react, postgres, flutter…"
        value={draft}
        onChange={(e) => setDraft(e.target.value)}
        onKeyDown={(e) => {
          if (e.key !== 'Enter' && e.key !== ',') return;
          e.preventDefault();
          add(draft);
        }}
        onBlur={() => add(draft)}
      />
      <datalist id="tech-suggestions">
        {suggestions.map((tag) => (
          <option key={tag} value={tag} />
        ))}
      </datalist>
    </div>
  );
}

function AboutEditor({
  project,
  onDone,
}: {
  project: ProjectView;
  onDone: () => void;
}) {
  const update = useUpdateProject();
  const { data: users = [] } = useUsers();
  const [about, setAbout] = useState<About>({ ...EMPTY_ABOUT, ...(project.about ?? {}) });
  const [summary, setSummary] = useState<Record<string, unknown> | undefined>(
    project.about?.summary,
  );

  useEffect(() => {
    setAbout({ ...EMPTY_ABOUT, ...(project.about ?? {}) });
    setSummary(project.about?.summary);
  }, [project]);

  async function save(): Promise<void> {
    try {
      await update.mutateAsync({ id: project._id, patch: { about: { ...about, summary } } });
      toast.success('About saved');
      onDone();
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : 'Could not save');
    }
  }

  return (
    <div className="flex flex-col gap-3" data-testid="about-editor">
      <div className="flex flex-col gap-1.5">
        <Label>Summary</Label>
        <RichTextEditor
          data-testid="about-summary"
          value={summary}
          onChange={setSummary}
          placeholder="What is this project, in a paragraph a newcomer can read?"
        />
      </div>

      <div className="flex flex-col gap-1.5">
        <Label htmlFor="about-type">Type</Label>
        <select
          id="about-type"
          data-testid="about-type"
          className="h-8 w-[220px] rounded-[6px] border border-[var(--border)] bg-[var(--surface)] px-2 text-[13px]"
          value={about.projectType ?? ''}
          onChange={(e) =>
            setAbout({ ...about, projectType: (e.target.value || undefined) as ProjectType })
          }
        >
          <option value="">Not set</option>
          {PROJECT_TYPES.map((t) => (
            <option key={t} value={t}>
              {TYPE_LABEL[t]}
            </option>
          ))}
        </select>
      </div>

      <div className="flex flex-col gap-1.5">
        <Label>Tech stack</Label>
        <TechStackEditor
          value={about.techStack}
          onChange={(techStack) => setAbout({ ...about, techStack })}
        />
      </div>

      <div className="flex flex-col gap-1.5">
        <Label>Links</Label>
        {about.links.map((link, i) => (
          <div key={i} className="flex gap-1.5">
            <Input
              aria-label={`Link ${i + 1} label`}
              className="w-[160px]"
              placeholder="Repository"
              value={link.label}
              onChange={(e) => {
                const links = about.links.map((l, j) =>
                  j === i ? { ...l, label: e.target.value } : l,
                );
                setAbout({ ...about, links });
              }}
            />
            <Input
              aria-label={`Link ${i + 1} URL`}
              placeholder="https://…"
              value={link.url}
              onChange={(e) => {
                const links = about.links.map((l, j) => (j === i ? { ...l, url: e.target.value } : l));
                setAbout({ ...about, links });
              }}
            />
            <Button
              variant="ghost"
              size="icon-sm"
              aria-label={`Remove link ${i + 1}`}
              onClick={() => setAbout({ ...about, links: about.links.filter((_, j) => j !== i) })}
            >
              <X />
            </Button>
          </div>
        ))}
        <Button
          variant="subtle"
          size="sm"
          className="self-start"
          data-testid="add-link"
          onClick={() => setAbout({ ...about, links: [...about.links, { label: '', url: '' }] })}
        >
          <Plus />
          Add a link
        </Button>
      </div>

      <div className="flex flex-col gap-1.5">
        <Label>Contacts</Label>
        {about.contacts.map((contact, i) => (
          <div key={i} className="flex gap-1.5">
            <select
              aria-label={`Contact ${i + 1}`}
              className="h-8 w-[200px] rounded-[6px] border border-[var(--border)] bg-[var(--surface)] px-2 text-[13px]"
              value={contact.userId}
              onChange={(e) => {
                const contacts = about.contacts.map((c, j) =>
                  j === i ? { ...c, userId: e.target.value } : c,
                );
                setAbout({ ...about, contacts });
              }}
            >
              {users.map((u) => (
                <option key={u._id} value={u._id}>
                  {u.name}
                </option>
              ))}
            </select>
            <Input
              aria-label={`Contact ${i + 1} role`}
              placeholder="Client contact"
              value={contact.role}
              onChange={(e) => {
                const contacts = about.contacts.map((c, j) =>
                  j === i ? { ...c, role: e.target.value } : c,
                );
                setAbout({ ...about, contacts });
              }}
            />
            <Button
              variant="ghost"
              size="icon-sm"
              aria-label={`Remove contact ${i + 1}`}
              onClick={() =>
                setAbout({ ...about, contacts: about.contacts.filter((_, j) => j !== i) })
              }
            >
              <X />
            </Button>
          </div>
        ))}
        <Button
          variant="subtle"
          size="sm"
          className="self-start"
          data-testid="add-contact"
          disabled={users.length === 0}
          onClick={() =>
            setAbout({
              ...about,
              contacts: [...about.contacts, { userId: users[0]._id, role: '' }],
            })
          }
        >
          <Plus />
          Add a contact
        </Button>
      </div>

      <div className="flex justify-end gap-2">
        <Button variant="ghost" onClick={onDone}>
          Cancel
        </Button>
        <Button variant="primary" data-testid="save-about" disabled={update.isPending} onClick={save}>
          {update.isPending && <Loader2 className="animate-spin" />}
          Save
        </Button>
      </div>
    </div>
  );
}

function AboutView({ project }: { project: ProjectView }) {
  const { data: users = [] } = useUsers();
  const about = project.about;
  const byId = new Map(users.map((u) => [u._id, u]));

  const isEmpty =
    !about ||
    (!about.summary &&
      !about.projectType &&
      about.techStack.length === 0 &&
      about.links.length === 0 &&
      about.contacts.length === 0);

  if (isEmpty) {
    return (
      <p className="text-[13px] vt-muted" data-testid="about-empty">
        Nobody has written this up yet. A paragraph here saves every newcomer the
        same conversation.
      </p>
    );
  }

  return (
    <div className="flex flex-col gap-3" data-testid="about-view">
      {about.projectType && (
        <div>
          <Badge tone="accent" data-testid="about-type-badge">
            {TYPE_LABEL[about.projectType]}
          </Badge>
        </div>
      )}

      {about.summary && (
        <RichTextEditor data-testid="about-summary-view" value={about.summary} editable={false} />
      )}

      {about.techStack.length > 0 && (
        <div className="flex flex-wrap gap-1.5" data-testid="tech-chips">
          {about.techStack.map((tag) => (
            <Badge key={tag} tone="neutral">
              {tag}
            </Badge>
          ))}
        </div>
      )}

      {about.links.length > 0 && (
        <ul className="flex flex-col gap-1" data-testid="about-links">
          {about.links.map((link) => (
            <li key={link.url}>
              <a
                href={link.url}
                target="_blank"
                rel="noreferrer noopener"
                className="inline-flex items-center gap-1.5 text-[13px] text-[var(--accent)] hover:underline"
              >
                <ExternalLink className="size-3.5" />
                {link.label || link.url}
              </a>
            </li>
          ))}
        </ul>
      )}

      {about.contacts.length > 0 && (
        <ul className="flex flex-col gap-1.5" data-testid="about-contacts">
          {about.contacts.map((contact) => {
            const person = byId.get(contact.userId);
            return (
              <li key={`${contact.userId}-${contact.role}`} className="flex items-center gap-2 text-[13px]">
                <UserAvatar name={person?.name ?? 'Unknown'} id={contact.userId} size={22} />
                <span className="font-medium">{person?.name ?? 'Unknown'}</span>
                <span className="vt-muted">{contact.role}</span>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}

function StatsRow({ projectId }: { projectId: string }) {
  const { data: stats } = useProjectStats(projectId);
  if (!stats) return null;

  const cells: { label: string; value: string | number }[] = [
    { label: 'Total', value: stats.totalTasks },
    { label: 'Open', value: stats.openTasks },
    { label: 'Overdue', value: stats.overdueTasks },
    { label: 'Done this week', value: stats.completedThisWeek },
  ];

  return (
    <div className="grid gap-3 sm:grid-cols-4" data-testid="project-stats">
      {cells.map((cell) => (
        <div key={cell.label} className="vt-surface p-3">
          <div className="text-[11px] uppercase tracking-wide vt-muted">{cell.label}</div>
          <div className="vt-mono text-[22px] font-semibold">{cell.value}</div>
        </div>
      ))}
    </div>
  );
}

export function ProjectOverviewPage() {
  const { key = '' } = useParams();
  const { data: project, isLoading } = useProject(key);
  const can = useCan();
  const [editing, setEditing] = useState(false);

  if (isLoading || !project) {
    return (
      <>
        <PageHeader title="Overview" />
        <div className="max-w-[900px] p-4">
          <Skeleton className="h-64" />
        </div>
      </>
    );
  }

  const canEdit = can(PERMISSIONS.PROJECT_UPDATE);

  return (
    <>
      <PageHeader
        title={`${project.icon} ${project.name}`}
        subtitle={project.description || 'Everything a newcomer needs, in one screen.'}
      />

      <div className="flex max-w-[900px] flex-col gap-4 p-4">
        <StatsRow projectId={project._id} />

        <ProjectGuideCard projectId={project._id} />

        <Section
          title="About"
          action={
            canEdit && !editing ? (
              <Button variant="subtle" size="sm" data-testid="edit-about" onClick={() => setEditing(true)}>
                <Pencil />
                Edit
              </Button>
            ) : null
          }
        >
          {editing ? (
            <AboutEditor project={project} onDone={() => setEditing(false)} />
          ) : (
            <AboutView project={project} />
          )}
        </Section>

        <Section title="Files">
          {/* Project-scoped attachments: the brief, the contract, the brand kit
              — things that belong to the project rather than to one task. */}
          <AttachmentSection
            entityId={project._id}
            entityType="project"
            readOnly={!canEdit}
            title="Project files"
          />
        </Section>
      </div>
    </>
  );
}
